import type { ToolCore, ToolModelDef } from "@loumy/contracts";
import { selectModel } from "@loumy/contracts";
import type { ZodTypeAny } from "zod";
import { titleCase } from "../lib/format";
import { categoryMeta, orderCategories } from "../registry/categories";
import { getCore, listCategories, listCores } from "../registry/toolRegistry";
import { defaultsOf, introspectSettings, type SettingsField } from "../settings/introspect";
import { applyModelSwitch } from "../settings/modelControls";

// D123 Globale Suche (§11) — PURE Logik der Type-to-search-Palette, React-frei (testbar,
// wie modelControls/introspect). Drei Bausteine:
//   • Index    — Such-Ziele: alle Tools (Registry, §4 — nichts hart referenziert) + Screens.
//   • Parser   — „<Ziel> <Parameter…>": längstes Token-Präfix matcht einen Namen, der Rest
//                wird GENERISCH gegen das Zod-Settings-Schema + core.models geparst (D12-SSOT:
//                introspectSettings liefert Felder/Bounds, core.models die Modell-Labels).
//                NUR Parameter (kein Ziel-Treffer) → aufs aktuell offene Tool anwenden.
//   • Plan     — deklaratives Ausführungs-Rezept { navigateTo, toolId, modelId, patch }:
//                die Palette setzt den Draft VOR dem Navigate (setDraft, D65-Mechanik) —
//                der SettingsPanel-Default-Seed greift nur bei fehlendem Draft.

// ── Index ────────────────────────────────────────────────────────────────────────

export interface ScreenEntry {
  type: "screen";
  id: string;
  /** names[0] = Anzeige-Label, Rest = Such-Aliasse. */
  names: string[];
  path: string;
  icon: string;
}

export interface ToolEntry {
  type: "tool";
  core: ToolCore;
  names: string[];
  icon: string;
}

/** D126: Job als Such-Ziel — Titel (D98) als Name, Enter = D65-Mechanik (openJob + Navigation).
 *  `path` wird beim Index-Bau aus der Registry aufgelöst; Jobs ohne Core (deprecated Tool,
 *  D32) gehören gar nicht erst in den Index (wie der deaktivierte JobsList-Klick). */
export interface JobEntry {
  type: "job";
  id: string;
  names: string[];
  path: string;
  icon: string;
  detail?: string;
}

/** D126: Ordner als Such-Ziel — Enter wählt ihn aus (selectFolder) + öffnet die Assets-Ansicht.
 *  `id` null = Inbox (virtueller folder_id-IS-NULL-View, §11/D20). */
export interface FolderEntry {
  type: "folder";
  id: string | null;
  names: string[];
  icon: string;
}

export type IndexEntry = ScreenEntry | ToolEntry | JobEntry | FolderEntry;

// App-Screens (SideRail-Nav, §11) — statisch: die Rail ist die einzige Quelle dieser Flächen.
const SCREENS: ScreenEntry[] = [
  { type: "screen", id: "generate", names: ["Generate"], path: "/generate", icon: "generate" },
  // D139: „Studio" zeigt jetzt auf das Asset-Studio (Bearbeiten), nicht mehr auf /generate.
  { type: "screen", id: "studio", names: ["Studio", "Bearbeiten", "Edit"], path: "/studio", icon: "scissors" },
  { type: "screen", id: "assets", names: ["Assets"], path: "/assets", icon: "assets" },
  { type: "screen", id: "editor", names: ["Editor", "Canvas"], path: "/editor", icon: "canvas" },
  { type: "screen", id: "explore", names: ["Explore"], path: "/explore", icon: "explore" },
  // D165: API-Keys + MCP-/Plugin-Installation eigener Screen; „API" sucht hier (nicht mehr Account).
  { type: "screen", id: "mcp", names: ["MCP", "API", "Plugin", "Integration", "API-Key"], path: "/mcp", icon: "api" },
  { type: "screen", id: "account", names: ["Account", "Konto", "Credits", "Plan"], path: "/account", icon: "user" },
];

/**
 * Such-Index: Tools (Registry-Order = Tab-Order) + Screens + dynamische Einträge (D126:
 * Folder/Jobs aus dem Query-Cache — die Palette reicht sie herein, die Logik bleibt pur).
 * `uiNames` sind die echten Anzeige-Namen der LAZY WebToolUIs (z.B. „Image Generation") —
 * solange sie nicht geladen sind, trägt `titleCase(id)` (z.B. „Image Generator"); beide
 * bleiben als Aliasse suchbar. Reihenfolge = Ranking-Tiebreaker: Tools > Screens > Folder > Jobs.
 */
export function buildIndex(
  uiNames?: ReadonlyMap<string, string>,
  dynamic?: { folders?: readonly FolderEntry[]; jobs?: readonly JobEntry[] },
): IndexEntry[] {
  const tools: ToolEntry[] = listCores().map((core) => {
    const names = [...new Set([uiNames?.get(core.id), titleCase(core.id), core.id].filter((n): n is string => Boolean(n)))];
    return { type: "tool", core, names, icon: categoryMeta(core.category).icon };
  });
  return [...tools, ...SCREENS, ...(dynamic?.folders ?? []), ...(dynamic?.jobs ?? [])];
}

// ── Fuzzy-Match ──────────────────────────────────────────────────────────────────

function norm(s: string): string {
  return s.toLowerCase();
}

/** Match-Qualität der Such-Nadel gegen EINEN Namen (0 = kein Treffer). */
function nameScore(needle: string, name: string): number {
  const n = norm(name);
  if (n === needle) return 100;
  if (n.startsWith(needle)) return 80;
  // alle Nadel-Wörter matchen der Reihe nach Wort-Anfänge („im ge" → „Image Generation")
  const nameWords = n.split(/[\s_-]+/);
  const needleWords = needle.split(" ");
  let wi = 0;
  let ordered = true;
  for (const w of needleWords) {
    while (wi < nameWords.length && !nameWords[wi]?.startsWith(w)) wi++;
    if (wi >= nameWords.length) {
      ordered = false;
      break;
    }
    wi++;
  }
  if (ordered) return 60;
  if (n.includes(needle)) return 40;
  return 0;
}

function entryScore(needle: string, entry: IndexEntry): number {
  let best = 0;
  for (const name of entry.names) best = Math.max(best, nameScore(needle, name));
  return best;
}

// ── Parameter-Grammatik ──────────────────────────────────────────────────────────

export interface ParsedParams {
  /** Modell-Wahl (D101) — wird beim Ausführen über applyModelSwitch gemerged, nie roh gesetzt. */
  modelId?: string;
  patch: Record<string, unknown>;
  /** Menschenlesbare Fragmente je Treffer („Modell: Loco Shift" · „duration: 5") — Suggestion-Detail. */
  summary: string[];
  /** Alle nicht konsumierten Tokens (= hart abgelehnte + freie) — Score-Malus. */
  unmatched: string[];
  /** D126: FREI gebliebene Tokens (Teilmenge von unmatched, Query-Reihenfolge, Original-
   *  Schreibweise) — Kandidaten für die Feld-Varianten-Suggestions („prompt: …" / „action: …").
   *  Die Palette rät NIE still ein Ziel-Feld (User-Korrektur D126): der User wählt per
   *  Suggestion. Hart abgelehnte Tokens (kv mit kaputtem Wert, „5s" ohne duration) sind
   *  bewusst NICHT dabei. */
  free: string[];
}

/** Bester Modell-Treffer für ein Token (id/Label/Label-Wörter, prefix-tolerant) — D101-Labels.
 *  `score` 100 = exakter id/Label-Treffer (für die Token-Präzedenz: exakt VOR Enum-Optionen,
 *  Präfix/Wort erst NACH exakten Enum-Treffern — Codex-P2: keine stille Modell-Kaperung). */
function matchModel(models: readonly ToolModelDef[], token: string): { m: ToolModelDef; score: number } | undefined {
  let best: { m: ToolModelDef; score: number } | undefined;
  for (const m of models) {
    const score = Math.max(nameScore(token, m.id), nameScore(token, m.label));
    if (score > 0 && (!best || score > best.score)) best = { m, score };
  }
  return best;
}

function clampNumber(f: SettingsField, value: number): number {
  let v = value;
  if (f.min !== undefined) v = Math.max(f.min, v);
  if (f.max !== undefined) v = Math.min(f.max, v);
  return f.int ? Math.round(v) : v;
}

const TRUE_WORDS = new Set(["true", "on", "1", "ja", "yes", "an"]);
const FALSE_WORDS = new Set(["false", "off", "0", "nein", "no", "aus"]);

/** Wert eines `key=value`-Tokens gegen den Feld-Kind parsen (undefined = nicht parsebar).
 *  `raw` kommt in ORIGINAL-Schreibweise (D126: String-Felder case-erhaltend übernehmen);
 *  bool/number/enum normalisieren intern. */
function parseFieldValue(f: SettingsField, raw: string): unknown {
  const value = norm(raw);
  if (f.kind === "boolean") {
    if (TRUE_WORDS.has(value)) return true;
    if (FALSE_WORDS.has(value)) return false;
    return undefined;
  }
  if (f.kind === "number") {
    // STRENG vollständig matchen (Codex-P2: parseFloat schluckt „35%"/„5foo" still);
    // nur `duration` erlaubt das Sekunden-Suffix („duration=5s").
    const cleaned = f.key === "duration" ? value.replace(/(s|sec|sek)$/i, "") : value;
    if (!/^\d+(?:[.,]\d+)?$/.test(cleaned)) return undefined;
    return clampNumber(f, Number.parseFloat(cleaned.replace(",", ".")));
  }
  if (f.kind === "enum") {
    const exact = f.options?.find((o) => norm(o) === value);
    if (exact !== undefined) return exact;
    const pref = (f.options ?? []).filter((o) => norm(o).startsWith(value));
    return pref.length === 1 ? pref[0] : undefined;
  }
  return raw; // string: Original-Schreibweise übernehmen (z.B. action=walk, prompt=…)
}

/**
 * Token-Liste GENERISCH gegen Schema + Modelle parsen — Tokens der Reihe nach, erste
 * greifende Regel gewinnt: (1) `key=value`/`key:value` (Feld-Key exakt oder eindeutiges
 * Präfix; `model=`/`modell=` wählt das Modell), (2) FELD-ADRESSIERUNG per Leerzeichen (D126,
 * User-Korrektur): „prompt ein schweinchen" / „loop off" / „duration 5" — das Token matcht
 * einen Feld-Key, der WERT folgt dahinter (String-Felder nehmen GREEDY den ganzen Rest —
 * mehrwortige Prompts; andere Kinds genau ein Token), (3) EXAKTER Modell-Name (D101 —
 * „loco-shift"), (4) EXAKTE Enum-Option („16:9", „green"), (5) Modell-Präfix/-Wort
 * („shift" → Loco Shift), (6) eindeutiges Enum-Präfix (ab 2 Zeichen), (7) Boolean-Feldname
 * („loop" → true), (8) Zahl mit optionalem Sekunden-Suffix („5s" verlangt ein `duration`-
 * Feld; nackte Zahl → `duration`, sonst das EINZIGE Zahlenfeld). Exakt-vor-Präfix über die
 * Kategorien hinweg (Codex-P2): ein Token, das eine Enum-Option EXAKT trifft, wird nie
 * still als Modell-Präfix gekapert. Zahlen werden an die statischen Schema-Bounds geklemmt
 * (Submit validiert ohnehin gegen die provider-gebundenen Bounds, D44/D101). Im GEWÄHLTEN
 * Modell versteckte Felder werden am Ende gestrippt (withModelGating-Spiegel, Codex-P1 —
 * der Draft soll zeigen, was der Submit wirklich sieht). Übrig bleibende Tokens werden NIE
 * still einem Feld zugeschlagen (User-Korrektur D126) — sie landen in `free`, und die
 * Palette bietet je passendem Feld eine eigene Suggestion an.
 */
export function parseParams(core: ToolCore, tokens: string[]): ParsedParams {
  const models = core.models ?? [];
  // `model` läuft über matchModel/applyModelSwitch (Modell-Defaults mergen!), nie als rohes Enum-Feld.
  const fields = introspectSettings(core.settingsSchema as unknown as ZodTypeAny).filter(
    (f) => !(models.length > 0 && f.key === "model"),
  );
  // Zuweisungen in Insertion-Order sammeln (späteres Token zum selben Feld gewinnt) —
  // patch/summary entstehen erst NACH dem Hidden-Stripping des gewählten Modells.
  const assignments = new Map<string, { f: SettingsField; value: unknown }>();
  let model: ToolModelDef | undefined;
  const unmatched: string[] = [];
  // D126: kv-Tokens mit BEKANNTEM Key, aber kaputtem Wert („duration=5foo", „model=xyz") —
  // sichtbar abgelehnt statt still in den Freitext-Prompt gespült (klar strukturierter Intent).
  const hardUnmatched: string[] = [];

  const setField = (f: SettingsField, value: unknown) => {
    assignments.delete(f.key);
    assignments.set(f.key, { f, value });
  };

  /** Enum-Option-Treffer über alle Enum-Felder: exakt bzw. (ab 2 Zeichen) EINDEUTIGES Präfix. */
  const enumHit = (token: string, exactOnly: boolean): { f: SettingsField; o: string } | undefined => {
    const candidates: { f: SettingsField; o: string }[] = [];
    for (const f of fields) {
      if (f.kind !== "enum") continue;
      for (const o of f.options ?? []) {
        if (norm(o) === token) return { f, o };
        if (!exactOnly && token.length >= 2 && norm(o).startsWith(token)) candidates.push({ f, o });
      }
    }
    return !exactOnly && candidates.length === 1 ? candidates[0] : undefined;
  };

  // Tokens kommen in ORIGINAL-Schreibweise (D126: Freitext/String-Werte case-erhaltend);
  // gematcht wird über die normalisierte Form `t`. Index-Schleife: die Feld-Adressierung
  // (Regel 2) konsumiert Folge-Tokens als Wert.
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i] ?? "";
    const t = norm(token);

    // (1) key=value / key:value — Key muss ein Feld (oder model) treffen, sonst fällt das
    // Token durch (so bleibt „16:9" eine Enum-Option statt eines kaputten key:value).
    const kv = /^([\p{L}\p{N}_-]+)[=:](.+)$/u.exec(token);
    if (kv) {
      const key = norm(kv[1] ?? "");
      const rawValue = kv[2] ?? ""; // Original-Case — parseFieldValue normalisiert je Kind
      if (models.length > 0 && (key === "model" || key === "modell")) {
        const m = matchModel(models, norm(rawValue));
        if (m) {
          model = m.m;
          continue;
        }
        hardUnmatched.push(token); // model=<unbekannt> → hart ablehnen (kein Freitext)
        continue;
      }
      const exact = fields.find((f) => norm(f.key) === key);
      // Codex-P2 (D126-R1): Präfix-Keys erst ab 2 Zeichen (wie die Leerzeichen-Syntax) —
      // „d=5" soll kein stilles duration-Alias sein.
      const prefixed = exact ? [exact] : key.length >= 2 ? fields.filter((f) => norm(f.key).startsWith(key)) : [];
      if (prefixed.length === 1 && prefixed[0]) {
        const v = parseFieldValue(prefixed[0], rawValue);
        if (v !== undefined) {
          setField(prefixed[0], v);
          continue;
        }
        hardUnmatched.push(token); // Key bekannt, Wert unparsebar → hart ablehnen
        continue;
      }
      // kein Feld-Treffer → weiter mit den positionalen Regeln (z.B. „16:9")
    }

    // (2) FELD-ADRESSIERUNG per Leerzeichen (D126, User-Korrektur: „prompt ein schweinchen"
    // setzt prompt="ein schweinchen" — das Feldname-Token gehört NICHT in den Wert):
    // Token matcht einen Feld-Key (exakt; Präfix ab 2 Zeichen) UND es folgt mindestens ein
    // Token → String-Felder nehmen GREEDY den ganzen Rest (mehrwortige Prompts), andere
    // Kinds genau das nächste Token (parsebar, sonst fällt das Token normal durch).
    if (i + 1 < tokens.length) {
      if (models.length > 0 && (t === "model" || t === "modell")) {
        const m = matchModel(models, norm(tokens[i + 1] ?? ""));
        if (m) {
          model = m.m;
          i += 1;
          continue;
        }
        // Codex-P1 (D126-R1): „model <unbekannt>" = klar strukturierter FEHL-Intent → beide
        // Tokens hart ablehnen (nie Freitext, keine ausführbare Suggestion).
        hardUnmatched.push(`${token} ${tokens[i + 1] ?? ""}`);
        i += 1;
        continue;
      }
      const exactKey = fields.find((f) => norm(f.key) === t);
      const keyCands = exactKey ? [exactKey] : t.length >= 2 ? fields.filter((f) => norm(f.key).startsWith(t)) : [];
      const f = keyCands.length === 1 ? keyCands[0] : undefined;
      if (f) {
        if (f.kind === "string") {
          setField(f, tokens.slice(i + 1).join(" "));
          i = tokens.length; // Rest komplett konsumiert (greedy)
          continue;
        }
        const v = parseFieldValue(f, tokens[i + 1] ?? "");
        if (v !== undefined) {
          setField(f, v);
          i += 1;
          continue;
        }
        // Codex-P1 (D126-R1): adressiertes Zahl-/Enum-Feld mit unparsebarem Wert („duration
        // 5foo") → Paar hart ablehnen und KONSUMIEREN — sonst fiele der Feldname weich durch.
        // BEWUSSTE Ausnahme Boolean: der nackte Switch-Name bedeutet „an" (D123 „auch switchs");
        // „loop maybe" setzt also loop=true und lässt „maybe" als sichtbaren Freitext weiter.
        if (f.kind !== "boolean") {
          hardUnmatched.push(`${token} ${tokens[i + 1] ?? ""}`);
          i += 1;
          continue;
        }
      }
    }

    // (3) EXAKTER Modell-Name (id/Label, D101): „loco-shift"
    const modelMatch = models.length > 0 ? matchModel(models, t) : undefined;
    if (modelMatch && modelMatch.score === 100) {
      model = modelMatch.m;
      continue;
    }

    // (4) EXAKTE Enum-Option: „16:9", „green"
    const exactEnum = enumHit(t, true);
    if (exactEnum) {
      setField(exactEnum.f, exactEnum.o);
      continue;
    }

    // (5) Modell-Präfix/-Wort: „shift" → Loco Shift, „loco" → erstes Loco-Modell.
    // Mindestens Wort-Anfang-Qualität (≥60) UND ≥2 Zeichen — Substring-Treffer (40) würden
    // sonst Freitext-Wörter kapern („im", „o", … D126).
    if (modelMatch && modelMatch.score >= 60 && t.length >= 2) {
      model = modelMatch.m;
      continue;
    }

    // (6) eindeutiges Enum-Präfix (ab 2 Zeichen, gegen Tipp-Rauschen): „gre" → green
    const prefixEnum = enumHit(t, false);
    if (prefixEnum) {
      setField(prefixEnum.f, prefixEnum.o);
      continue;
    }

    // (7) Boolean-Feldname → true („loop"; false via „loop off"/key=off)
    const boolField = fields.find((f) => f.kind === "boolean" && norm(f.key) === t);
    if (boolField) {
      setField(boolField, true);
      continue;
    }

    // (8) Zahl mit optionalem Sekunden-Suffix
    const num = /^(\d+(?:[.,]\d+)?)(s|sec|sek)?$/i.exec(t);
    if (num) {
      const value = Number.parseFloat((num[1] ?? "").replace(",", "."));
      const duration = fields.find((f) => f.kind === "number" && f.key === "duration");
      const numeric = fields.filter((f) => f.kind === "number");
      // Suffix „s" MEINT Sekunden → ohne duration-Feld kein Treffer (statt z.B. count=5).
      const target = duration ?? (num[2] ? undefined : numeric.length === 1 ? numeric[0] : undefined);
      if (target && Number.isFinite(value)) {
        setField(target, clampNumber(target, value));
        continue;
      }
      if (num[2]) {
        // Sekunden-Suffix ohne duration-Feld = strukturierter Intent → hart ablehnen,
        // nicht still in den Freitext-Prompt spülen (D126; nackte Zahlen bleiben weich —
        // die sind plausibler Prompt-Inhalt, z.B. „Ritter mit 3 Schwertern").
        hardUnmatched.push(token);
        continue;
      }
    }

    unmatched.push(token); // Original-Schreibweise — wird `free` (Feld-Varianten, D126)
  }

  // Codex-P1: im GEWÄHLTEN Modell versteckte Felder strippen (withModelGating-Spiegel) —
  // „image generation portrait 16:9" setzt KEIN aspectRatio (loco-portrait fixiert 2:3).
  if (model) for (const key of model.hiddenSettings ?? []) assignments.delete(key);

  const out: ParsedParams = {
    patch: {},
    summary: [],
    unmatched: [...hardUnmatched, ...unmatched],
    free: [...unmatched],
  };
  if (model) {
    out.modelId = model.id;
    out.summary.push(`Modell: ${model.label}`);
  }
  for (const { f, value } of assignments.values()) {
    out.patch[f.key] = value;
    out.summary.push(`${f.key}: ${String(value)}`);
  }
  return out;
}

// ── Suggestions ──────────────────────────────────────────────────────────────────

/** Deklaratives Ausführungs-Rezept einer Suggestion (die Palette führt es aus). */
export interface ExecutionPlan {
  navigateTo?: string;
  toolId?: string;
  modelId?: string;
  patch?: Record<string, unknown>;
  /** Tool per Suche ÖFFNEN = frischer Compose: openedJob schließen — sonst könnte eine noch
   *  ausstehende D65-Rehydrierung (useOpenedJob lädt asynchron) den Palette-Draft überschreiben. */
  freshCompose?: boolean;
  /** D126: Job öffnen (D65-Mechanik wie der JobsList-Klick — openJob + Navigation zum Tool;
   *  die Settings/Inputs-Rehydrierung macht useOpenedJob). */
  openJobId?: string;
  /** D126: Ordner auswählen (`id` null = Inbox) — wirkt auf FolderTree/Assets (selectedFolderId). */
  selectFolder?: { id: string | null };
  /** D138: Query-VERVOLLSTÄNDIGUNG (Zahl-Feld ohne Wert) — die Palette ersetzt die Query
   *  durch diesen Text und bleibt OFFEN (der User tippt den Wert), statt etwas auszuführen. */
  completeQuery?: string;
}

export interface Suggestion {
  kind: "tool" | "screen" | "apply" | "job" | "folder" | "complete";
  /** stabiler React-Key, z.B. "tool:spritesheet". */
  id: string;
  title: string;
  detail?: string;
  icon: string;
  score: number;
  plan: ExecutionPlan;
}

const MAX_SUGGESTIONS = 8;
/** Malus je nicht-parsebarem Rest-Token — tippende User (halbe Wörter) werfen den Treffer
 *  nicht ab, klar falsche Quereinträge rutschen aber unter bessere Matches. */
const UNMATCHED_PENALTY = 12;

function toolSuggestion(e: ToolEntry, p: ParsedParams, score: number, idSuffix = ""): Suggestion {
  const core = e.core;
  return {
    kind: "tool",
    id: `tool:${core.id}${idSuffix}`,
    title: e.names[0] ?? core.id,
    detail: p.summary.length > 0 ? p.summary.join(" · ") : categoryMeta(core.category).label,
    icon: e.icon,
    score,
    plan: {
      navigateTo: `/generate/${core.category}/${core.id}`,
      toolId: core.id,
      modelId: p.modelId,
      patch: p.patch,
      freshCompose: true,
    },
  };
}

const EMPTY_PARAMS: ParsedParams = { patch: {}, summary: [], unmatched: [], free: [] };

/** D126: hart abgelehnte Tokens (kv kaputt, „5s" ohne duration) — frei Gebliebenes zählt
 *  nicht, das absorbieren die Feld-Varianten. */
function hardCount(p: ParsedParams): number {
  return p.unmatched.length - p.free.length;
}

/** Codex-P2 (D126-R1): EFFEKTIVES Modell für den Hidden-Filter = explizite Palette-Wahl
 *  ODER das Modell des übergebenen (Bestands-)Drafts — die Suggestion soll nichts anzeigen,
 *  was draftForPlan/Submit später strippen (withModelGating-Spiegel auch in der VORSCHAU). */
function effectiveHidden(core: ToolCore, p: ParsedParams, draft?: Record<string, unknown>): ReadonlySet<string> {
  const models = core.models ?? [];
  if (models.length === 0) return new Set<string>();
  const m = p.modelId ? models.find((x) => x.id === p.modelId) : selectModel(models, draft ?? {});
  return new Set<string>(m?.hiddenSettings ?? []);
}

/** Versteckte Felder aus patch UND summary entfernen („Modell: …"-Fragment bleibt immer). */
function stripHidden(p: ParsedParams, hidden: ReadonlySet<string>): ParsedParams {
  if (hidden.size === 0) return p;
  const patch = Object.fromEntries(Object.entries(p.patch).filter(([key]) => !hidden.has(key)));
  const summary = p.summary.filter((s) => {
    const key = s.slice(0, Math.max(0, s.indexOf(":")));
    return key === "Modell" || !hidden.has(key);
  });
  return { ...p, patch, summary };
}

/**
 * D126 (User-Korrektur „prompt sollte NICHT immer angenommen werden"): bleibt nach dem
 * strukturierten Parsen FREITEXT übrig, rät die Palette das Ziel-Feld NICHT — sie baut je
 * passendem Feld eine eigene Parameter-Variante („prompt: ein schweinchen" / „action: ein
 * schweinchen"), und der USER wählt per Suggestion. Kandidaten = String-Felder (nur die
 * können mehrwortigen Text aufnehmen; Schema-Reihenfolge = Anzeige-Reihenfolge), abzüglich
 * bereits gesetzter und versteckter (`hidden` — effektives Modell). Kein String-Feld →
 * keine Varianten (der Rest bleibt unmatched, Score-Malus).
 */
function fieldVariants(core: ToolCore, p: ParsedParams, hidden: ReadonlySet<string>): ParsedParams[] {
  if (p.free.length === 0) return [];
  const text = p.free.join(" ");
  const rejected = p.unmatched.slice(0, hardCount(p));
  return introspectSettings(core.settingsSchema as unknown as ZodTypeAny)
    .filter((f) => f.kind === "string" && f.key !== "model" && !hidden.has(f.key) && !(f.key in p.patch))
    .map((f) => ({
      modelId: p.modelId,
      patch: { ...p.patch, [f.key]: text },
      summary: [...p.summary, `${f.key}: ${text}`],
      unmatched: rejected,
      free: [],
    }));
}

/**
 * D138 Feldnamen-AUTOCOMPLETE (User: „bei manchen dingen muss ich voll angeben, es gibt
 * keine suggestions — z.B. parameter audio"): ist das LETZTE Query-Token das EINZIGE freie
 * Token und (ab 2 Zeichen) PRÄFIX eines Nicht-String-Feld-Keys, schlägt die Palette das
 * FELD selbst vor — Boolean → apply-Variante „an" (Spiegel der nackten-Name-Regel, D123/
 * D126: „aus" weiterhin via „audio off"), Enum → eine Variante JE Option (der User wählt,
 * D126), Number → Query-Vervollständigung (der Wert fehlt noch: `params` bleibt leer, die
 * Palette ersetzt das angefangene Token durch den Key und bleibt offen). String-Felder
 * NICHT (dort ist Freitext der WERT — das decken die fieldVariants ab, die den Feldnamen
 * ohnehin sichtbar machen); `model` läuft über matchModel (Regel 3/5); bereits gesetzte +
 * modell-versteckte Felder raus. Nur das LETZTE Token, weil nur dort gerade GETIPPT wird —
 * früherer Freitext ist Prompt-Kandidat, kein angefangener Feldname.
 */
interface FieldNameVariant {
  key: string;
  /** fertige apply-Variante (bool/enum) — fehlt bei number (nur Vervollständigung). */
  params?: ParsedParams;
}

function fieldNameVariants(core: ToolCore, p: ParsedParams, hidden: ReadonlySet<string>, tokens: string[]): FieldNameVariant[] {
  if (p.free.length !== 1 || p.free[0] !== tokens[tokens.length - 1]) return [];
  const t = norm(p.free[0] ?? "");
  if (t.length < 2) return [];
  const withValue = (f: SettingsField, value: unknown): ParsedParams => ({
    modelId: p.modelId,
    patch: { ...p.patch, [f.key]: value },
    summary: [...p.summary, `${f.key}: ${String(value)}`],
    unmatched: [],
    free: [],
  });
  const out: FieldNameVariant[] = [];
  for (const f of introspectSettings(core.settingsSchema as unknown as ZodTypeAny)) {
    if (f.kind === "string" || f.key === "model" || hidden.has(f.key) || f.key in p.patch) continue;
    if (!norm(f.key).startsWith(t)) continue;
    if (f.kind === "boolean") out.push({ key: f.key, params: withValue(f, true) });
    else if (f.kind === "enum") for (const o of f.options ?? []) out.push({ key: `${f.key}=${o}`, params: withValue(f, o) });
    else out.push({ key: f.key }); // number → Vervollständigung
  }
  return out;
}

/** D138: Vervollständigungs-Suggestion für ein Zahl-Feld — Enter ersetzt das angefangene
 *  letzte Token durch den Feld-Key (+ Leerzeichen), die Palette bleibt offen. */
function completeSuggestion(id: string, key: string, tokens: string[], score: number): Suggestion {
  return {
    kind: "complete",
    id,
    title: key,
    detail: "Wert eingeben …",
    icon: "wand",
    score,
    plan: { completeQuery: `${[...tokens.slice(0, -1), key].join(" ")} ` },
  };
}

function screenSuggestion(e: ScreenEntry, score: number): Suggestion {
  return {
    kind: "screen",
    id: `screen:${e.id}`,
    title: e.names[0] ?? e.id,
    icon: e.icon,
    score,
    plan: { navigateTo: e.path },
  };
}

function jobSuggestion(e: JobEntry, score: number): Suggestion {
  return {
    kind: "job",
    id: `job:${e.id}`,
    title: e.names[0] ?? "Job",
    detail: e.detail,
    icon: e.icon,
    score,
    plan: { openJobId: e.id, navigateTo: e.path },
  };
}

function folderSuggestion(e: FolderEntry, score: number): Suggestion {
  return {
    kind: "folder",
    id: `folder:${e.id ?? "inbox"}`,
    title: e.names[0] ?? "Ordner",
    detail: "Ordner",
    icon: e.icon,
    score,
    plan: { selectFolder: { id: e.id }, navigateTo: "/assets" },
  };
}

/** Suggestion für einen Eintrag OHNE Parameter-Rest (Browse-Modus + Nicht-Tool-Treffer). */
function plainSuggestion(e: IndexEntry, score: number): Suggestion {
  if (e.type === "tool") return toolSuggestion(e, EMPTY_PARAMS, score);
  if (e.type === "screen") return screenSuggestion(e, score);
  if (e.type === "job") return jobSuggestion(e, score);
  return folderSuggestion(e, score);
}

/**
 * Suggestions für eine Query. Leere Query = Browse-Modus (alle Ziele, Index-Order).
 * Sonst: (a) NUR-Parameter-Vorschlag fürs aktuell offene Tool (`currentToolId`), wenn
 * mindestens ein Token als Parameter parsebar ist; (b) je Index-Eintrag das LÄNGSTE
 * Token-Präfix, das den Namen matcht — Rest-Tokens werden bei Tools als Parameter geparst,
 * Screens/Folder/Jobs nehmen keine. Stabil nach Score sortiert (Index-Order als Tiebreaker:
 * Tools > Screens > Folder > Jobs). Tokens behalten die ORIGINAL-Schreibweise (D126:
 * Freitext → prompt case-erhaltend); gematcht wird normalisiert.
 */
export function buildSuggestions(
  query: string,
  index: IndexEntry[],
  currentToolId?: string,
  /** Codex-P2 (D126-R1): Bestands-Draft des aktuellen Tools — Hidden-Filter der
   *  apply-Suggestions gegen das EFFEKTIVE Modell (Palette-Wahl ODER Draft-Modell). */
  currentDraft?: Record<string, unknown>,
): Suggestion[] {
  const tokens = query.trim().split(/\s+/).filter(Boolean);
  const out: Suggestion[] = [];

  if (tokens.length === 0) {
    for (const e of index) out.push(plainSuggestion(e, 1));
    return out.slice(0, MAX_SUGGESTIONS);
  }

  // (a) Nur Parameter → aktuelles Tool (z.B. „loco-shift 5s" auf dem SpriteSheet-Screen).
  // D126: bleibt Freitext übrig, eine Suggestion JE Ziel-Feld-Kandidat (der User wählt) —
  // Freitext-Deutungen rangieren unter Wort-Anfang-Treffern (60), damit „edi" den Editor
  // öffnet statt den Prompt zu ändern; gezielt geparste Queries behalten 75. Codex-P1:
  // hart abgelehnter Fehl-Intent („duration=5foo") → KEINE ausführbare Suggestion.
  if (currentToolId) {
    const core = getCore(currentToolId);
    if (core) {
      const hiddenRaw = parseParams(core, tokens);
      const hidden = effectiveHidden(core, hiddenRaw, currentDraft);
      const p = stripHidden(hiddenRaw, hidden);
      const entry = index.find((e): e is ToolEntry => e.type === "tool" && e.core.id === core.id);
      const title = `Auf ${entry?.names[0] ?? titleCase(core.id)} anwenden`;
      const applyOf = (v: ParsedParams, score: number, idSuffix = ""): Suggestion => ({
        kind: "apply",
        id: `apply:${core.id}${idSuffix}`,
        title,
        detail: v.summary.join(" · "),
        icon: "wand",
        score,
        plan: { toolId: core.id, modelId: v.modelId, patch: v.patch },
      });
      if (hardCount(p) === 0) {
        // D138: Feldnamen-Autocomplete — das angefangene letzte Token matcht Nicht-String-
        // Feld-Keys; rankt ÜBER den Freitext-Deutungen (55 — ein Feldnamen-Präfix ist das
        // stärkere Signal), aber UNTER Wort-Anfang-Treffern (60: „edi" öffnet den Editor).
        for (const nv of fieldNameVariants(core, p, hidden, tokens)) {
          out.push(nv.params ? applyOf(nv.params, 58, `:name:${nv.key}`) : completeSuggestion(`complete:apply:${core.id}:${nv.key}`, nv.key, tokens, 58));
        }
        const variants = fieldVariants(core, p, hidden);
        if (variants.length > 0) {
          for (const v of variants) {
            const key = Object.keys(v.patch).find((k) => !(k in p.patch)) ?? "field";
            out.push(applyOf(v, 55, `:${key}`));
          }
        } else if (p.summary.length > 0) {
          out.push(applyOf(p, 75 - UNMATCHED_PENALTY * p.unmatched.length));
        }
      }
    }
  }

  // (b) Tools/Screens/Folder/Jobs — längstes matchendes Token-Präfix gewinnt je Eintrag.
  // D126: Freitext-Rest → eine Tool-Suggestion je Ziel-Feld-Kandidat (leichter Deutungs-
  // Malus); ohne Kandidaten bleibt der Rest unmatched (voller Malus). Codex-P1: hart
  // abgelehnter Fehl-Intent → keine Suggestion für dieses Tool (sichtbare Ablehnung).
  for (const e of index) {
    for (let k = tokens.length; k >= 1; k--) {
      const needle = norm(tokens.slice(0, k).join(" "));
      const s = entryScore(needle, e);
      if (s === 0) continue;
      const rest = tokens.slice(k);
      if (e.type !== "tool") {
        if (rest.length > 0) continue; // nur Tools nehmen Parameter
        out.push(plainSuggestion(e, s));
        break;
      }
      const raw = parseParams(e.core, rest);
      // Hidden-Filter gegen das EXPLIZIT gewählte bzw. Default-Modell; ist der Eintrag das
      // AKTUELLE Tool, zählt dessen Bestands-Draft (D138-Codex-P1: sonst schlüge z.B.
      // „spritesheet loo" unter loco-shift versteckte loop*-Felder vor). Drafts FREMDER
      // Tools sind hier unbekannt — draftForPlan strippt beim Ausführen erneut.
      const hidden = effectiveHidden(e.core, raw, e.core.id === currentToolId ? currentDraft : undefined);
      const p = stripHidden(raw, hidden);
      if (hardCount(p) > 0) break; // Fehl-Intent → sichtbar KEINE Suggestion
      // D138: Feldnamen-Autocomplete auch hinter dem Tool-Namen („spritesheet au") —
      // gleicher 3-Punkte-Abstand über den Freitext-Deutungen (s−10) wie in Zweig (a).
      for (const nv of fieldNameVariants(e.core, p, hidden, tokens)) {
        out.push(nv.params ? toolSuggestion(e, nv.params, s - 7, `:name:${nv.key}`) : completeSuggestion(`complete:tool:${e.core.id}:${nv.key}`, nv.key, tokens, s - 7));
      }
      const variants = fieldVariants(e.core, p, hidden);
      if (variants.length > 0) {
        for (const v of variants) {
          const key = Object.keys(v.patch).find((kk) => !(kk in p.patch)) ?? "field";
          out.push(toolSuggestion(e, v, s - 10, `:${key}`));
        }
      } else {
        out.push(toolSuggestion(e, p, s - UNMATCHED_PENALTY * p.unmatched.length));
      }
      break;
    }
  }

  return out
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_SUGGESTIONS);
}

// ── Ausführung ───────────────────────────────────────────────────────────────────

/**
 * Draft fürs Plan-Ausführen: bestehender Draft (User-Eingaben wie der Prompt ÜBERLEBEN —
 * die Suche überschreibt nur, was sie explizit setzt) bzw. Schema-Defaults (defaultsOf,
 * derselbe Seed wie SettingsPanel, D12-SSOT); dann Modell-Wechsel über applyModelSwitch
 * (Modell-Defaults mergen hart, D101), dann die expliziten Parameter (User-Intent gewinnt
 * über Modell-Defaults — dieselbe Präzedenz wie withModelGating beim Submit).
 */
export function draftForPlan(
  core: ToolCore,
  existing: Record<string, unknown> | undefined,
  plan: ExecutionPlan,
): Record<string, unknown> {
  const models = core.models ?? [];
  let draft = existing ?? defaultsOf(core.settingsSchema as unknown as ZodTypeAny);
  if (plan.modelId && models.length > 0) {
    draft = applyModelSwitch(models, draft, plan.modelId);
  }
  let patch = plan.patch ?? {};
  if (models.length > 0) {
    // Codex-P1 (withModelGating-Spiegel): im EFFEKTIVEN Modell (Palette-Wahl ODER Bestand —
    // parseParams kennt den Bestands-Draft nicht) versteckte Felder NICHT in den Draft
    // schreiben — der Submit strippt sie deterministisch, der Draft soll den echten
    // Submit-Zustand zeigen statt einen Wert, der still verworfen wird.
    const hidden = new Set<string>(selectModel(models, draft).hiddenSettings ?? []);
    patch = Object.fromEntries(Object.entries(patch).filter(([key]) => !hidden.has(key)));
  }
  return { ...draft, ...patch };
}

/**
 * Aktives Tool aus dem Pfad — SPIEGELT die Studio-Auflösung (Studio.tsx: ungültige
 * Kategorie → erste, fehlendes/ungültiges Tool → erstes der Kategorie), damit der
 * NUR-Parameter-Vorschlag exakt das Tool trifft, das der User gerade sieht.
 * Nicht-Studio-Pfade (Assets/Editor/…) → undefined (dort gibt es keinen Settings-Draft).
 */
export function currentToolIdFromPath(pathname: string): string | undefined {
  const m = /^\/generate(?:\/([^/]+)(?:\/([^/]+))?)?\/?$/.exec(pathname);
  if (!m) return undefined;
  const categories = orderCategories(listCategories());
  const category = m[1] && listCores(m[1]).length > 0 ? m[1] : (categories[0] ?? "");
  const tools = listCores(category);
  return m[2] && tools.some((t) => t.id === m[2]) ? m[2] : tools[0]?.id;
}
